import React, { useState } from "react";
import supabase from "../../utils/supabase";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useT } from "../../stores/uiLang";

// Run E3 B4: this modal follows the UI language (EN text unchanged).
const EA_T = {
  en: {
    startReq: "Start date is required",
    startTimeReq: "Start time is required",
    dueReq: "Due date is required",
    dueTimeReq: "Due time is required",
    dueAfter: "Due date must be after start date",
    fixErrors: "Please fix the errors before submitting",
    updateFail: "Failed to update assignment. Please try again.",
    statusFail: "Assignment dates updated, but failed to update student statuses.",
    updated: "Assignment dates updated successfully.",
    statusChanged: " Status automatically changed from 'overdue' to 'in progress' for all students.",
    title: "Edit Assignment Dates",
    usingCreation: "⚠️ Using assignment creation date as start date",
    startLabel: "Start Date & Time",
    fromCreation: "(from creation date)",
    noStartNote: "This assignment was created without a start date, so the creation date is being used as the default start date.",
    dueLabel: "Due Date & Time",
    mustBeAfter: (d: string) => `Must be after start date: ${d}`,
    pickStart: "Please select a start date first",
    willChange: "Status will change:",
    note: "Note:",
    willChangeBody: "All overdue student statuses will automatically change from 'overdue' to 'in progress' when you save.",
    noteBody: "If you set the due date to a future date, all overdue student statuses will automatically change from 'overdue' to 'in progress'.",
    summary: "Summary of Changes",
    sumStart: "• Start date will be updated",
    sumDue: "• Due date will be updated",
    sumStatus: "• All overdue student assignment statuses will change from \"overdue\" to \"in progress\"",
    cancel: "Cancel",
    updating: "Updating...",
    update: "Update Dates",
  },
  es: {
    startReq: "La fecha de inicio es obligatoria",
    startTimeReq: "La hora de inicio es obligatoria",
    dueReq: "La fecha de entrega es obligatoria",
    dueTimeReq: "La hora de entrega es obligatoria",
    dueAfter: "La fecha de entrega debe ser después de la fecha de inicio",
    fixErrors: "Corrige los errores antes de guardar",
    updateFail: "No se pudo actualizar la tarea. Inténtalo otra vez.",
    statusFail: "Se actualizaron las fechas de la tarea, pero no se pudo actualizar el estado de los estudiantes.",
    updated: "Las fechas de la tarea se actualizaron.",
    statusChanged: " El estado de todos los estudiantes cambió de \"vencida\" a \"en curso\".",
    title: "Cambiar las fechas de la tarea",
    usingCreation: "⚠️ Se usa la fecha de creación de la tarea como fecha de inicio",
    startLabel: "Fecha y hora de inicio",
    fromCreation: "(de la fecha de creación)",
    noStartNote: "Esta tarea se creó sin fecha de inicio, por eso se usa la fecha de creación como fecha de inicio.",
    dueLabel: "Fecha y hora de entrega",
    mustBeAfter: (d: string) => `Debe ser después de la fecha de inicio: ${d}`,
    pickStart: "Primero elige una fecha de inicio",
    willChange: "El estado va a cambiar:",
    note: "Nota:",
    willChangeBody: "Al guardar, el estado de todos los estudiantes con la tarea vencida cambiará de \"vencida\" a \"en curso\".",
    noteBody: "Si pones una fecha de entrega futura, el estado de todos los estudiantes con la tarea vencida cambiará de \"vencida\" a \"en curso\".",
    summary: "Resumen de los cambios",
    sumStart: "• Se cambiará la fecha de inicio",
    sumDue: "• Se cambiará la fecha de entrega",
    sumStatus: "• El estado de todas las tareas vencidas de los estudiantes cambiará de \"vencida\" a \"en curso\"",
    cancel: "Cancelar",
    updating: "Guardando...",
    update: "Guardar fechas",
  },
};

interface EditAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignment: any;
  onAssignmentUpdated: () => void;
}

export default function EditAssignmentModal({
  isOpen,
  onClose,
  assignment,
  onAssignmentUpdated,
}: EditAssignmentModalProps) {
  const t = useT(EA_T);
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [dueTime, setDueTime] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    startDate?: string;
    dueDate?: string;
  }>({});
  const [hasOverdueStudents, setHasOverdueStudents] = useState(false);

  // Check if status will change from overdue to in_progress
  const willStatusChange = () => {
    if (dueDate && dueTime) {
      const newDueDate = dayjs(`${dueDate}T${dueTime}`);
      const now = dayjs();
      return newDueDate.isAfter(now);
    }
    return false;
  };

  // Check if there are overdue students for this assignment
  const checkOverdueStudents = async () => {
    if (!assignment) return;

    try {
      const { data: overdueStudents, error } = await supabase
        .from("student_assignments")
        .select("id")
        .eq("assignment_id", assignment.id)
        .eq("status", "overdue");

      if (error) {
        console.error("Error checking overdue students:", error);
        return;
      }

      setHasOverdueStudents(overdueStudents && overdueStudents.length > 0);
    } catch (err) {
      console.error("Error checking overdue students:", err);
    }
  };

  // Initialize form with current assignment dates when modal opens
  React.useEffect(() => {
    if (assignment && isOpen) {
      // Use start_date if it exists, otherwise use created_at as fallback
      const startDateTime = assignment.start_date
        ? new Date(assignment.start_date)
        : new Date(assignment.created_at || Date.now());
      const dueDateTime = new Date(assignment.due_date);

      const startDateStr = startDateTime.toISOString().split("T")[0];
      const startTimeStr = startDateTime.toTimeString().slice(0, 5);
      const dueDateStr = dueDateTime.toISOString().split("T")[0];
      const dueTimeStr = dueDateTime.toTimeString().slice(0, 5);

      setStartDate(startDateStr);
      setStartTime(startTimeStr);
      setDueDate(dueDateStr);
      setDueTime(dueTimeStr);
      setErrors({});

      // Check for overdue students
      checkOverdueStudents();
    }
  }, [assignment, isOpen]);

  const handleStartDateChange = (date: string) => {
    setStartDate(date);
    // Reset due date if it's before the new start date
    if (dueDate && date && dueTime) {
      const newStartDateTime = dayjs(`${date}T${startTime}`);
      const currentDueDateTime = dayjs(`${dueDate}T${dueTime}`);

      if (
        currentDueDateTime.isSame(newStartDateTime) ||
        currentDueDateTime.isBefore(newStartDateTime)
      ) {
        setDueDate("");
        setDueTime("");
      }
    }
    // Clear any due date errors
    if (errors.dueDate) {
      setErrors((prev) => ({ ...prev, dueDate: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!startDate) {
      newErrors.startDate = t.startReq;
    } else if (!startTime) {
      newErrors.startDate = t.startTimeReq;
    }

    if (!dueDate) {
      newErrors.dueDate = t.dueReq;
    } else if (!dueTime) {
      newErrors.dueDate = t.dueTimeReq;
    } else if (startDate && dueDate) {
      const startDateTime = dayjs(`${startDate}T${startTime}`);
      const dueDateTime = dayjs(`${dueDate}T${dueTime}`);

      if (
        dueDateTime.isSame(startDateTime) ||
        dueDateTime.isBefore(startDateTime)
      ) {
        newErrors.dueDate = t.dueAfter;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t.fixErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      // Check if we need to update status from overdue to in_progress
      const newDueDate = dayjs(`${dueDate}T${dueTime}`);
      const now = dayjs();
      const willBeInFuture = newDueDate.isAfter(now);

      // Convert dates to ISO8601 UTC format to avoid timezone issues
      const startDateTime = dayjs(`${startDate}T${startTime}:00`);
      const dueDateTime = dayjs(`${dueDate}T${dueTime}:00`);

      // Prepare update data
      const updateData: any = {
        start_date: startDateTime.utc().toISOString(),
        due_date: dueDateTime.utc().toISOString(),
      };

      // First update the assignment
      const { error: assignmentError } = await supabase
        .from("assignments")
        .update(updateData)
        .eq("id", assignment.id);

      if (assignmentError) {
        console.error("Error updating assignment:", assignmentError);
        toast.error(t.updateFail);
        return;
      }

      // If there are overdue students and new due date is in the future, update their status to in_progress
      if (hasOverdueStudents && willBeInFuture) {
        const { error: studentAssignmentsError } = await supabase
          .from("student_assignments")
          .update({ status: "in_progress" })
          .eq("assignment_id", assignment.id)
          .eq("status", "overdue");

        if (studentAssignmentsError) {
          console.error(
            "Error updating student assignments:",
            studentAssignmentsError
          );
          toast.error(t.statusFail);
          onAssignmentUpdated();
          onClose();
          return;
        }
      }

      let successMessage = t.updated;
      if (hasOverdueStudents && willBeInFuture) {
        successMessage += t.statusChanged;
      }

      toast.success(successMessage);
      onAssignmentUpdated();
      onClose();
    } catch (err) {
      console.error("Error updating assignment:", err);
      toast.error(t.updateFail);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !assignment) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {t.title}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {assignment.assessment_name}
            </p>
            {!assignment.start_date && (
              <p className="text-xs text-blue-600 mt-1">
                {t.usingCreation}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            disabled={isSubmitting}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form className="space-y-6" onSubmit={handleSubmit}>
          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t.startLabel}
              {!assignment.start_date && (
                <span className="text-xs text-blue-600 ml-2">
                  {t.fromCreation}
                </span>
              )}
            </label>
            <div className="mt-1 flex space-x-2">
              <input
                type="date"
                className={`w-1/2 border rounded-md shadow-sm ${
                  errors.startDate ? "border-red-300" : "border-gray-300"
                }`}
                onChange={(e) => handleStartDateChange(e.target.value)}
                disabled={isSubmitting}
                value={startDate}
              />
              <input
                type="time"
                className="w-1/2 border-gray-300 rounded-md shadow-sm"
                onChange={(e) => setStartTime(e.target.value)}
                disabled={isSubmitting}
                value={startTime}
              />
            </div>
            {errors.startDate && (
              <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>
            )}
            {!assignment.start_date && (
              <p className="mt-1 text-xs text-gray-500">
                {t.noStartNote}
              </p>
            )}
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t.dueLabel}
            </label>
            {startDate && (
              <p className="text-xs text-gray-500 mt-1">
                {t.mustBeAfter(startDate)}
              </p>
            )}
            <div className="mt-1 flex space-x-2">
              <input
                type="date"
                className={`w-1/2 border rounded-md shadow-sm ${
                  errors.dueDate ? "border-red-300" : "border-gray-300"
                } ${!startDate ? "bg-gray-100 cursor-not-allowed" : ""}`}
                onChange={(e) => setDueDate(e.target.value)}
                disabled={isSubmitting || !startDate}
                min={startDate || new Date().toISOString().split("T")[0]}
                value={dueDate}
              />
              <input
                type="time"
                className={`w-1/2 border-gray-300 rounded-md shadow-sm ${
                  !startDate ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                onChange={(e) => setDueTime(e.target.value)}
                disabled={isSubmitting || !startDate}
                value={dueTime}
              />
            </div>
            {!startDate && (
              <p className="mt-1 text-sm text-gray-500">
                {t.pickStart}
              </p>
            )}
            {errors.dueDate && (
              <p className="mt-1 text-sm text-red-600">{errors.dueDate}</p>
            )}

            {/* Status change notification */}
            {hasOverdueStudents && (
              <div
                className={`mt-2 p-2 border rounded-md ${
                  willStatusChange()
                    ? "bg-green-50 border-green-200"
                    : "bg-blue-50 border-blue-200"
                }`}
              >
                <p
                  className={`text-xs ${
                    willStatusChange() ? "text-green-700" : "text-blue-700"
                  }`}
                >
                  <span className="font-medium">
                    {willStatusChange() ? t.willChange : t.note}
                  </span>{" "}
                  {willStatusChange()
                    ? t.willChangeBody
                    : t.noteBody}
                </p>
              </div>
            )}
          </div>

          {/* Summary of changes */}
          {hasOverdueStudents && willStatusChange() && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
              <h4 className="text-sm font-medium text-green-800 mb-1">
                {t.summary}
              </h4>
              <ul className="text-xs text-green-700 space-y-1">
                <li>{t.sumStart}</li>
                <li>{t.sumDue}</li>
                <li>{t.sumStatus}</li>
              </ul>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-small btn-secondary"
              disabled={isSubmitting}
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              className="btn btn-small"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t.updating}
                </div>
              ) : (
                t.update
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
